'use client';

import React, { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ChartContainer } from '@/components/chart-container';
import { MarketDataDatasource } from '@/lib/market-data-datasource';
import {
  computeInitialRange,
  fetchBarsForRange,
  dateToViewportUnix,
} from '@/lib/market-data-utils';
import { applyStudies } from '@/lib/tradingview-studies';

interface AssetChartProps {
  symbol: string;
  assetType: string;
  interval?: string;
  start?: string;
  end?: string;
  chartType?: 'candlestick' | 'line';
  studies?: string;
}

type ChartState =
  | 'loading'
  | 'success'
  | 'error_no_data'
  | 'error_no_data_in_range'
  | 'error_retryable'
  | 'error_permanent';

const RETRYABLE_STATUSES = [429, 500, 502, 503, 504];
const RESOLUTION_MAP: Record<string, string> = { '1d': '1D', '1h': '60', '5m': '5' };
const MAX_EXPANSION_RETRIES = 2;

export function AssetChart({
  symbol,
  assetType,
  interval = '1d',
  start,
  end,
  chartType = 'candlestick',
  studies,
}: AssetChartProps) {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<TradingViewWidget | null>(null);
  const [state, setState] = useState<ChartState>('loading');
  const [errorDetail, setErrorDetail] = useState('');

  const doMount = async (signal: AbortSignal) => {
    setState('loading');

    // Always fetch with the default window for the interval.
    // start/end are viewport hints only — they control setVisibleRange,
    // not the data fetch. This avoids narrow ranges that trigger API errors.
    const range = computeInitialRange(interval);

    // Determine expansion anchor direction
    let expandDirection: 'backward' | 'forward' = 'backward';
    if (start && !end) expandDirection = 'forward';

    let bars: Awaited<ReturnType<typeof fetchBarsForRange>> | null = null;
    let currentFrom = range.from;
    let currentTo = range.to;

    // Preflight fetch with expansion retries
    try {
      bars = await fetchBarsForRange(assetType, symbol, interval, currentFrom, currentTo, { maxBars: 500 });

      if (bars.bars.length === 0) {
        // Expansion retries: widen the window to find data (handles off-hours, weekends)
        for (let retry = 0; retry < MAX_EXPANSION_RETRIES; retry++) {
          if (signal.aborted) return;

          const originalWidth = range.to - range.from;
          const expandedWidth = originalWidth * Math.pow(3, retry + 1);

          if (expandDirection === 'backward') {
            currentFrom = range.to - expandedWidth;
            currentTo = range.to;
          } else {
            currentFrom = range.from;
            currentTo = range.from + expandedWidth;
          }

          bars = await fetchBarsForRange(assetType, symbol, interval, currentFrom, currentTo, { maxBars: 500 });
          if (bars.bars.length > 0) break;
        }

        if (bars.bars.length === 0) {
          console.log(`[AssetChart] No data found for ${symbol} after expansion retries`);
          setState('error_no_data');
          return;
        }
      }
    } catch (error: unknown) {
      if (signal.aborted) return;
      const status = (error as { status?: number }).status;

      if (status === 404) {
        console.log(`[AssetChart] 404 for ${symbol}: symbol not found in market-data-api`);
        setState('error_no_data');
        return;
      }
      if (status === 400 || status === 422) {
        console.error(`[AssetChart] ${status} for ${symbol}:`, error);
        setErrorDetail(String(error));
        setState('error_permanent');
        return;
      }
      if (status && RETRYABLE_STATUSES.includes(status)) {
        console.error(`[AssetChart] ${status} for ${symbol}:`, error);
        setState('error_retryable');
        return;
      }
      // Unknown error
      console.error(`[AssetChart] Fetch error for ${symbol}:`, error);
      setState('error_retryable');
      return;
    }

    if (signal.aborted) return;

    // Create datasource and seed with preflight bars.
    // start/end are viewport hints only — the datasource fetches freely.
    const datasource = new MarketDataDatasource({
      symbol,
      assetType,
      interval,
    });

    if (bars && bars.bars.length > 0) {
      datasource.seedBars(interval, bars.bars);
    }

    // Load TradingView library
    if (!window.TradingView) {
      const script = document.createElement('script');
      script.src = '/charting_library/charting_library.standalone.js';
      script.async = true;
      await new Promise<void>((resolve, reject) => {
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load TradingView library'));
        document.head.appendChild(script);
      });
    }

    if (signal.aborted || !chartContainerRef.current) return;

    const initialResolution = RESOLUTION_MAP[interval] || '1D';

    const widget = new window.TradingView.widget({
      container: chartContainerRef.current,
      library_path: '/charting_library/',
      datafeed: datasource,
      symbol: symbol,
      interval: initialResolution,
      fullscreen: false,
      autosize: true,
      height: 400,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      enabled_features: [
        'side_toolbar_in_fullscreen_mode',
        'header_resolutions',
      ],
      disabled_features: [
        'use_localstorage_for_settings',
        'volume_force_overlay',
        'header_saveload',
        'save_chart_properties_to_local_storage',
        'timeframes_toolbar',
        'header_screenshot',
        'display_market_status',
        'header_symbol_search',
        'symbol_search_hot_key',
        'header_settings',
        'header_indicators',
        'study_templates',
      ],
      overrides: {
        'mainSeriesProperties.style': chartType === 'line' ? 2 : 1,
        'mainSeriesProperties.showCountdown': false,
        'scalesProperties.showSeriesLastValue': true,
        'scalesProperties.showStudyLastValue': true,
      },
      theme: 'light',
      locale: 'en',
    } as never);

    widgetRef.current = widget;

    widget.onChartReady(() => {
      if (signal.aborted) return;

      // Set visible range when both start and end are provided
      if (start && end) {
        try {
          const chart = widget.activeChart();
          chart.setVisibleRange({
            from: dateToViewportUnix(start, 'start'),
            to: dateToViewportUnix(end, 'endInclusive'),
          });
        } catch (error) {
          console.warn('[AssetChart] setVisibleRange failed, using auto-fit:', error);
        }
      }

      // Apply technical studies
      if (studies) {
        try {
          const chart = widget.activeChart();
          applyStudies(chart, studies);
        } catch (error) {
          console.error('[AssetChart] Failed to apply studies:', error);
        }
      }

      setTimeout(() => {
        if (!signal.aborted) setState('success');
      }, 300);
    });
  };

  useEffect(() => {
    if (typeof window === 'undefined' || !chartContainerRef.current) return;

    const controller = new AbortController();
    doMount(controller.signal).catch((error) => {
      if (!controller.signal.aborted) {
        console.error('[AssetChart] Mount error:', error);
        setState('error_retryable');
      }
    });

    return () => {
      controller.abort();
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
          widgetRef.current = null;
        } catch (error) {
          console.error('[AssetChart] Error removing widget:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, assetType, interval, start, end, chartType, studies]);

  const handleRetry = () => {
    if (widgetRef.current) {
      try { widgetRef.current.remove(); } catch { /* ignore */ }
      widgetRef.current = null;
    }
    const controller = new AbortController();
    doMount(controller.signal).catch(() => setState('error_retryable'));
  };

  const showChartFrame = state === 'loading' || state === 'success';

  return (
    <ChartContainer>
      <div
        data-testid="asset-chart-frame"
        className="relative h-[320px] w-full rounded-lg border border-border bg-white shadow-sm sm:h-[400px]"
        style={{ display: showChartFrame ? 'block' : 'none' }}
      >
        <div
          ref={chartContainerRef}
          data-testid="asset-chart-container"
          className="h-full w-full overflow-hidden rounded-lg"
        />
        {state === 'loading' && (
          <Skeleton className="absolute inset-0 h-full w-full rounded-lg" />
        )}
      </div>

      {state === 'error_no_data' && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">No data available for {symbol}</p>
        </div>
      )}

      {state === 'error_no_data_in_range' && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">No data in range for {symbol}</p>
        </div>
      )}


      {state === 'error_retryable' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">Failed to load data for {symbol}</p>
          <button
            onClick={handleRetry}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {state === 'error_permanent' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">Failed to load chart for {symbol}</p>
          {errorDetail && (
            <p className="mt-1 text-xs text-red-600">{errorDetail}</p>
          )}
        </div>
      )}
    </ChartContainer>
  );
}
